import { useTranslation } from "react-i18next";
import { axiosInstance } from "api/axios";
import threadsService from "api/threadsService";
import { ECaseViews } from "components/CaseView/dto/ECaseViews";
import ChatCard from "components/CaseChatCard";
import Loading from "components/Loading";
import CaseChatContext from "contexts/CaseChatContext";
import ConverstationContext from "contexts/ConversationContext";
import KeysContext from "contexts/KeysContext";
import PackagesContext from "contexts/PackagesContext";
import { IAttachment } from "dto/IAttachment";
import { IPackage } from "dto/IPackage";
import { useContext, useEffect, useRef, useState } from "react";
import { useInfiniteQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import styles from "./styles.module.scss";
import { FC, Props } from "./typings";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { EOrder } from "components/SortOrder/typings";
import { faArrowDown, faArrowUp } from "@fortawesome/free-solid-svg-icons";
import usePDF from "hooks/usePDF";
import { SignalRContext } from "../../App";

const CaseChat: FC<Props> = ({
  caseId,
  createNewThread,
  thread,
  statuses,
  tags,
  hideMenu = false,
  setPack,
}) => {
  const { t } = useTranslation();
  const [editMode, setEditMode] = useState(false);
  const { view } = useParams();

  const separatorRef = useRef<HTMLDivElement>(null);

  const [order, setOrder] = useState(EOrder.DESCENDING);


  useEffect(() => {
    const item = localStorage.getItem("case_lastOrder2");
    if (!item) return;
    setOrder(item);
  }, []);

  const {
    data: packageData,
    fetchNextPage,
    isLoading: isL,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery(
    ["chat", order, view, caseId],
    async ({ pageParam }) => {
      const query = new URLSearchParams(window.location.search);

      if (view === ECaseViews.PACKAGES) {
        query.append("Kind", "12");
      } else {
        query.append("Kind", "4757");
      }
      query.append("CaseId", caseId!.toString());
      query.append("GroupThreads", "false");
      query.append("SortOrder", order);
      query.append("SortProp", "date");
      if (pageParam) {
        query.append("PageToken", pageParam);
      }

      const res = await axiosInstance.get(`/list?${query}`);
      return res.data;
    },
    {
      // enabled: false,
      staleTime: 0,
      cacheTime: 0,
      getNextPageParam: (lastPage) => {
        if (!lastPage.LastPage) {
          return lastPage.PageToken;
        }
        return undefined;
      },
    }
  );

  // const {
  //   data: packageData,
  //   refetch,
  //   isFetching,
  // } = listService.useGetPackage({
  //   Kind: '4831',
  //   CaseId: caseId,
  //   GroupThreads: false,
  // });
  const { isLoading } = threadsService.useUpdateList();

  const { setCasePackages } = useContext(CaseChatContext);
  const { packages, setPackages } = useContext(PackagesContext);

  // const packs: IPackage[] = useMemo(
  //   () => packageData?.data.Items ?? [],
  //   [packageData]
  // );

  const [selectedPackages, setSelectedPackages] = useState<IPackage[]>([]);
  // const [keys, setKeys] = useState<any[]>([]);

  const { keys, setKeys } = useContext(KeysContext);

  const navigate = useNavigate();

  const { converstation, setConverstation } = useContext(ConverstationContext);

  const compileData = async () => {
    console.log("Compile data");
    if (!packageData) return;

    const packs = await Promise.all(
      packageData?.pages
        .map((page: any) => {
          return page.Items;
        })
        .flat()
    );
    console.log(packs);

    setCasePackages(packs);
    setPackages(packs);
    setConverstation(packs);

    const objectKeys: any[] = await Promise.all(
      packageData?.pages
        .map((page: any) => {
          return page.ObjectKeys;
        })
        .flat()
    );

    const keys: any[] = await Promise.all(
      packageData?.pages
        .map((page: any) => {
          return page.Keys;
        })
        .flat()
    );

    //@ts-ignore
    setKeys((prev) =>
      [...prev, objectKeys, keys].filter((f) => f !== undefined).flat()
    );
  };

  const handleScroll = () => {
    if (!separatorRef.current) return;
    if (isL || isFetchingNextPage) return;
    const rect = separatorRef.current.getBoundingClientRect();
    const viewHeight = Math.max(
      document.documentElement.clientHeight,
      window.innerHeight
    );
    const isVisible = !(rect.bottom < 0 || rect.top - viewHeight >= 0);
    if (isVisible) {
      fetchNextPage();
    }
  };

  useEffect(() => {
    compileData();
  }, [packageData]);

  useEffect(() => {
    if (createNewThread) {
      setEditMode(true);
      setSelectedPackages([]);

      // setConverstation([]);
    }
  }, [createNewThread]);

  useEffect(() => {
    if (!editMode) {
      setSelectedPackages([]);
      return;
    }

    if (createNewThread) return;

    setSelectedPackages(
      packages.filter((pack) => pack.Thread?.Id === thread?.Id)
    );
  }, [editMode]);

  SignalRContext.useSignalREffect(
    "newMessages",
    (message: any) => {
      // jeśli wyświetlamy wyniki wyszukiwania, to nie dodajemy nowych przesyłek do listy
      if (!message.items) return;

      if (!message.items.length) return;

      const a: IPackage[] = message.items
        .filter((x) => x.case?.id === caseId)
        .map((m) => {
          return {
            isNew: true,
            Id: m.id,
            InternalId: m.InternalId,
            Kind: m.kind,
            Type: m.type,
            TypeId: 0,
            CreationDate: m.creationDate,
            LastModified: m.lastModified,
            SendDate: m.sendDate,
            ReceiveDate: m.receiveDate,
            ReturnDate: m.returnDate,
            Sender: m.sender,
            ReceivedBy: m.receivedBy,
            IsIncoming: m.isIncoming,
            IsForwarded: m.isForwarded,
            HasTasks: m.hasTasks,
            IsHandled: m.isHandled,
            IsDeskActive: m.isDeskActive,
            IsDraft: m.isDraft,
            IsSpam: m.isSpam,
            Unread: m.unread,
            AttachmentsCount: m.AttachmentsCount,
            TasksCount: 0,
            TagsCount: 0,
            TagIds: [],
            Tags: null,
            Tasks: null,
            Shares: null,
            Attachments:
              m.attachments != null
                ? m.attachments.map((a) => ({
                    Id: a.id,
                    Name: a.name,
                    FileName: a.fileName,
                    Label: a.label,
                    ContentType: a.contentType,
                    Format: a.format,
                    Size: a.size,
                    Uri: a.uri,
                    EncryptionKeyId: a.encryptionKeyId,
                    OwnerKind: a.ownerKind,
                    OwnerId: a.ownerId,
                  }))
                : null,
            Receivers:
              m.receivers != null
                ? m.receivers.map((r) => ({
                    Name: r.name,
                    Email: r.email,
                    SendStatus: r.sendStatus,
                    ParcelType: r.ParcelType,
                    Cc: r.cc,
                    Bcc: r.bcc,
                    IsMe: r.isMe,
                  }))
                : null,
            S1: m.s1,
            S2: m.s2,
            S3: m.s3,
            S4: m.s4,
            N1: m.n1,
            N2: m.n2,
            // Kategoria formy doręczenia (widok N3) — do bramkowania akcji
            // per-wiersz (np. „Potwierdź odbiór osobisty" = PersonalPickup).
            DeliveryCategory: m.deliveryCategory,
            EncryptionKeyId: m.encryptionKeyId,
            IsRemoved: false,
            RemovedDate: null,
            ShowInDossier: 0,
            UserId: m.userId,
            Case: m.case
              ? {
                  Id: m.case.id,
                  Client: m.case.client,
                  Signature: m.case.signature,
                  Title: m.case.title,
                }
              : null,
            Thread: m.thread
              ? {
                  Id: m.thread.id,
                  Label: m.thread.label,
                  Count: m.thread.count,
                  UnreadCount: m.thread.unreadCount,
                  LastMessageDirection: m.thread.lastMessageDirection,
                  HasIncomingMessages: m.thread.hasIncomingMessages,
                  HasOutgoingMessages: m.thread.hasOutgoingMessages,
                }
              : null,
            Dossier: null,
            CommentsCount: 0,
            StatusId: m.statusId,
            IsShared: false,
            SenderUserId: null,
            SenderParticipantId: null,
            SenderDetails: m.senderDetails
              ? {
                  Name: m.senderDetails.name,
                  Nip: m.senderDetails.nip,
                  REGON: m.senderDetails.rEGON,
                  KRS: m.senderDetails.kRS,
                  IsInstitution: m.senderDetails.isInstitution,
                  Email: m.senderDetails.email,
                  EDeliveryAddress: m.senderDetails.eDeliveryAddress,
                  //Address: IAddress;
                }
              : null,
          };
        });

      if (order == EOrder.ASCENDING) {
        setConverstation([...converstation, ...a]);
      } else {
        setConverstation([...a, ...converstation]);
      }
    },
    []
  );

  const handleChangePackages = (pack: IPackage, state: boolean) => {
    if (state) {
      setSelectedPackages([...selectedPackages, pack]);
      return;
    }
    setSelectedPackages(
      selectedPackages.filter((packe) => packe.Id !== pack.Id)
    );
  };

  const { blob, create, removeBlob } = usePDF();
  const [downloadFileName, setDownloadFileName] = useState("");


  const onDownload = async (pack: IPackage, attachment: IAttachment) => {
    setDownloadFileName(attachment.FileName);
    create(pack.Id, attachment, pack.Kind, true);
  };

  const onPreview = async (pack: IPackage, attachment: IAttachment) => {
    const isFullScreen = window.location.href.search("fullscreen") !== -1;

    const query = new URLSearchParams(window.location.search);

    if (isFullScreen && !query.get("fullscreen")) {
      query.append("fullscreen", "1");
    }

    if (!hideMenu) {
      navigate(
        `/cases/${caseId}/chat/${pack.Kind}/${pack.Id}/${attachment.Id}`
      );
    } else {
      navigate(
        `/fullscreen/cases/${caseId}/chat/${pack.Kind}/${pack.Id}/${attachment.Id}`
      );
    }
  };

  useEffect(() => {
    if (!blob) return;
    console.log("Downloading!");
    const URL = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = URL;
    link.setAttribute("download", downloadFileName);
    document.body.appendChild(link);
    link.click();
    window.URL.revokeObjectURL(URL);
    removeBlob();
  }, [blob]);

  const renderCardWithoutThread = () => {
    return (
      <div className={styles.other}>
        {converstation.map((Package, index) => (
          <ChatCard
            index={index}
            key={Package.Id}
            editMode={editMode}
            handleChangePackages={handleChangePackages}
            isSelected={
              !!selectedPackages.find((pack) => pack.Id === Package.Id)
            }
            setPack={setPack}
            refetch={refetch}
            Package={Package}
            statuses={statuses}
            tags={tags}
            keys={keys}
            onDownload={onDownload}
            onPreview={onPreview}
          />
        ))}
        <div ref={separatorRef} style={{ height: "5px" }}></div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className={styles.loadingWrapper}>
        <Loading />
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div className={styles.left}>
          <div className={styles.wrapper}>
            <div className={styles.card}>
              <div onClick={() => {}} className={styles.name}>
                {t("common.sortBy")}:{" "}
                {order === EOrder.DESCENDING
                  ? t("common.newestFirst")
                  : t("common.oldestFirst")}
              </div>
              <div
                onClick={() => {
                  const newOrder =
                    order === EOrder.ASCENDING
                      ? EOrder.DESCENDING
                      : EOrder.ASCENDING;

                  setOrder(newOrder);
                  localStorage.setItem("case_lastOrder2", newOrder);
                }}
                className={`${styles.arrow} `}
              >
                <FontAwesomeIcon
                  icon={order === EOrder.ASCENDING ? faArrowUp : faArrowDown}
                />
              </div>
            </div>
          </div>
        </div>
        {/* <div
          onClick={() => {
            setModals({ ...modals, createPost: true, hide: false });
          }}
          className={styles.right}
        >
          <SendIcon />
          Nowa przesyłka
        </div> */}
        <div className={styles.separator}></div>
      </div>
      <div onScroll={(e) => handleScroll?.(e)} className={styles.chat}>
        {renderCardWithoutThread()}
      </div>
    </div>
  );
};

export default CaseChat;
